import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "panel", "list", "currentLabel", "clearBtn", "error"]
  static values = { formsUrl: String, saveUrl: String, currentFormId: String, currentFormName: String, i18n: Object }

  _forms = []

  connect() {
    this._updateCurrentLabel()
  }

  async open() {
    this.panelTarget.hidden = false
    this.triggerTarget.hidden = true
    if (!this._forms.length) await this.loadForms()
  }

  close() {
    this.panelTarget.hidden = true
    this.triggerTarget.hidden = false
  }

  async loadForms() {
    this.listTarget.innerHTML = `<p class="text-xs text-muted py-2">${this._msg("loading", "Loading…")}</p>`
    if (this.hasErrorTarget) this.errorTarget.hidden = true

    try {
      const r = await fetch(this.formsUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const data = await r.json()

      if (data.error && !data.forms?.length) {
        this._showError(data.error)
        return
      }

      this._forms = data.forms || []

      if (!this._forms.length) {
        this.listTarget.innerHTML = `<p class="text-xs text-muted py-2">${this._msg("empty", "No lead forms found on your connected page.")}</p>`
        return
      }

      this._renderList()
    } catch {
      this._showError(this._msg("load_failed", "Failed to load forms — check your connection."))
    }
  }

  _renderList() {
    const leadsTpl = (this.hasI18nValue && this.i18nValue.leads_count_label) || "%{count} leads"
    this.listTarget.innerHTML = this._forms.map((f) => {
      const isActive = this.currentFormIdValue === f.id
      const leadsLabel = leadsTpl.replace("%{count}", f.leads_count ?? 0)
      return `<button type="button"
        class="w-full text-left rounded-lg border px-3 py-2.5 text-xs transition-colors duration-100 ${isActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-bg"}"
        data-form-id="${this._esc(f.id)}"
        data-form-name="${this._esc(f.name)}"
        data-action="click->meta-lead-form-picker#selectForm">
        <span class="font-medium text-ink block leading-snug">${this._esc(f.name)}</span>
        <span class="text-muted">${this._esc(f.status)} · ${this._esc(leadsLabel)}</span>
      </button>`
    }).join("")
  }

  async selectForm(event) {
    const { formId, formName } = event.currentTarget.dataset
    await this._save(formId, formName)
  }

  async clearForm() {
    await this._save("", "")
  }

  async _save(formId, formName) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    body.append("pro_listing[meta_form_id]", formId)
    body.append("pro_listing[meta_form_name]", formName)
    body.append("_method", "patch")

    try {
      const r = await fetch(this.saveUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
        credentials: "same-origin",
      })

      if (!r.ok) {
        this._showError(this._msg("save_failed", "Failed to save."))
        return
      }

      this.currentFormIdValue = formId
      this.currentFormNameValue = formName
      this._updateCurrentLabel()
      if (this._forms.length) this._renderList()
    } catch {
      this._showError(this._msg("save_connection_failed", "Failed to save — check your connection."))
    }
  }

  _updateCurrentLabel() {
    if (this.hasCurrentLabelTarget) {
      this.currentLabelTarget.textContent = this.currentFormNameValue || this.currentFormIdValue || ""
    }
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.hidden = !this.currentFormIdValue
    }
  }

  _showError(msg) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = msg
      this.errorTarget.hidden = false
    }
    this.listTarget.innerHTML = ""
  }

  _msg(key, fallback) {
    return (this.hasI18nValue && this.i18nValue[key]) || fallback
  }

  _esc(text) {
    return String(text ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
